const ARTISAN_YEARS_EXP_ID = 'artisan_years_experience'

const STATUS_FROM_LABEL = {
  Disponible: 'AVAILABLE',
  Occupé: 'BUSY',
  'En congé': 'ON_LEAVE',
}

const STATUS_TO_LABEL = {
  AVAILABLE: 'Disponible',
  BUSY: 'Occupé',
  ON_LEAVE: 'En congé',
}

const slugify = (value = '') =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')

const isValidHttpUrl = (value) => {
  if (!value || typeof value !== 'string') return false
  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

const toOptionalUrl = (value) => {
  const trimmed = value?.trim?.() ?? ''
  if (!trimmed) return undefined
  if (isValidHttpUrl(trimmed)) return trimmed
  const withProtocol = `https://${trimmed.replace(/^\/\//, '')}`
  return isValidHttpUrl(withProtocol) ? withProtocol : undefined
}

const normalizeCertificationName = (cert) =>
  typeof cert === 'string' ? cert.trim() : (cert?.name ?? '').trim()

const normalizeCategoryName = (service) =>
  typeof service === 'string' ? service.trim() : (service?.name ?? '').trim()

const parseYearsExperience = (experiences = []) => {
  const entry = experiences.find((exp) => exp.id === ARTISAN_YEARS_EXP_ID)
  if (!entry) return ''

  if (entry.description && /^\d+$/.test(String(entry.description).trim())) {
    return String(entry.description).trim()
  }

  const fromTitle = String(entry.title ?? '').match(/(\d+)/)
  return fromTitle?.[1] ?? ''
}

const parseAvailabilityFields = (availability = {}) => {
  const responseTime = availability.responseTime?.trim?.() ?? ''
  const statusLabel = STATUS_TO_LABEL[availability.status] ?? 'Disponible'

  if (!responseTime) {
    return {
      disponibilite: statusLabel,
      horaires: '',
    }
  }

  if (STATUS_FROM_LABEL[responseTime]) {
    return {
      disponibilite: responseTime,
      horaires: '',
    }
  }

  const parts = responseTime.split(' · ').map((part) => part.trim()).filter(Boolean)
  let disponibilite = statusLabel
  let horaires = ''
  const legacyParts = []

  for (const part of parts) {
    if (part.startsWith('disp:')) {
      disponibilite = part.slice(5).trim() || statusLabel
      continue
    }

    if (part.startsWith('hrs:')) {
      horaires = part.slice(4).trim()
      continue
    }

    if (STATUS_FROM_LABEL[part]) {
      disponibilite = part
      continue
    }

    legacyParts.push(part)
  }

  if (legacyParts.length === 1) {
    horaires = legacyParts[0]
  } else if (legacyParts.length >= 2) {
    disponibilite = legacyParts[0]
    horaires = legacyParts.slice(1).join(' · ')
  }

  return { disponibilite, horaires }
}

export const mapPortfolioToArtisanForm = (payload, fallbackUser = {}) => {
  const data = payload?.portfolioData ?? payload ?? {}
  const user = payload?.user ?? fallbackUser
  const availability = data.availability ?? {}
  const { disponibilite, horaires } = parseAvailabilityFields(availability)

  const categories = (data.services ?? [])
    .map(normalizeCategoryName)
    .filter(Boolean)

  const certifications = (data.certifications ?? [])
    .map(normalizeCertificationName)
    .filter(Boolean)

  return {
    nom_commercial: data.headline ?? '',
    description: data.description ?? '',
    categories,
    localisation: data.contact?.address ?? user.city ?? '',
    zone_intervention: Array.isArray(availability.zones) ? availability.zones : [],
    annees_experience: parseYearsExperience(data.experiences ?? []),
    certifications,
    horaires,
    telephone_pro: data.contact?.phone ?? user.phone ?? '',
    email_pro: data.contact?.email ?? user.email ?? '',
    site_web: data.contact?.website ?? '',
    disponibilite,
    meta: {
      is_verified: Boolean(user.isKycVerified),
      is_premium: Boolean(data.meta?.featured),
      is_visible: Boolean(data.meta?.publishedAt) && !data.meta?.hidden,
    },
  }
}

export const mapArtisanFormToPortfolio = (formData, existingPortfolio = {}) => {
  const existingServices = existingPortfolio.services ?? []
  const existingCertifications = existingPortfolio.certifications ?? []
  const existingExperiences = existingPortfolio.experiences ?? []
  const existingAvailability = existingPortfolio.availability ?? {}

  const services = (formData.categories ?? []).map((name) => {
    const trimmed = name.trim()
    const existing = existingServices.find((service) => service.name === trimmed)

    return {
      id: existing?.id ?? `svc_${slugify(trimmed)}`,
      name: trimmed,
      description: existing?.description ?? '',
      priceRange: existing?.priceRange,
    }
  })

  const certifications = (formData.certifications ?? []).map((cert) => {
    const name = normalizeCertificationName(cert)
    const existing = existingCertifications.find((item) => item.name === name)

    return {
      id: existing?.id ?? `cert_${slugify(name)}`,
      name,
      year: existing?.year,
      imageUrl: existing?.imageUrl,
    }
  })

  const otherExperiences = existingExperiences.filter(
    (exp) => exp.id !== ARTISAN_YEARS_EXP_ID
  )

  const years = String(formData.annees_experience ?? '').trim()
  const experiences = years
    ? [
        {
          id: ARTISAN_YEARS_EXP_ID,
          title: `${years} ans d'expérience`,
          company: 'Artisan',
          description: years,
        },
        ...otherExperiences,
      ]
    : otherExperiences

  const disponibilite = formData.disponibilite?.trim?.() ?? ''
  const horaires = formData.horaires?.trim?.() ?? ''
  const status = STATUS_FROM_LABEL[disponibilite] ?? existingAvailability.status ?? 'AVAILABLE'

  const responseTimeParts = []
  if (disponibilite && !STATUS_FROM_LABEL[disponibilite]) {
    responseTimeParts.push(`disp:${disponibilite}`)
  }
  if (horaires) {
    responseTimeParts.push(`hrs:${horaires}`)
  }

  const website = toOptionalUrl(formData.site_web)
  const email = formData.email_pro?.trim?.() || undefined
  const phone = formData.telephone_pro?.trim?.() || undefined
  const address = formData.localisation?.trim?.() || undefined

  return {
    headline: formData.nom_commercial?.trim?.() ?? '',
    description: formData.description?.trim?.() ?? '',
    services,
    certifications,
    experiences,
    contact: {
      phone,
      email,
      website,
      address,
    },
    availability: {
      status,
      zones: formData.zone_intervention ?? [],
      responseTime: responseTimeParts.join(' · ') || existingAvailability.responseTime || undefined,
    },
  }
}

export const validateArtisanForm = (formData) => {
  const errors = []

  if (!formData.nom_commercial?.trim()) {
    errors.push('Le nom commercial est requis')
  }

  if (!formData.description?.trim()) {
    errors.push('La description est requise')
  }

  if (!formData.annees_experience || Number(formData.annees_experience) < 0) {
    errors.push("Les années d'expérience sont requises")
  }

  if (!formData.categories?.length) {
    errors.push('Sélectionnez au moins une catégorie métier')
  }

  if (!formData.localisation?.trim()) {
    errors.push('La ville principale est requise')
  }

  if (!formData.telephone_pro?.trim()) {
    errors.push('Le téléphone professionnel est requis')
  }

  if (!formData.email_pro?.trim()) {
    errors.push("L'email professionnel est requis")
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email_pro.trim())) {
    errors.push("L'email professionnel n'est pas valide")
  }

  const website = formData.site_web?.trim?.()
  if (website && !toOptionalUrl(website)) {
    errors.push('Le site web doit être une URL valide')
  }

  return errors
}
